import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { CATEGORIES_MAP, CATEGORY_KEYS, CategoryKey, nativeCategoryForChain } from 'constants/tokenLists/categories'
import { useConnection } from 'hooks/useConnection'
import {
  useCurrencyFromMap,
  useTokenFromMapOrNetwork,
  useTokenFromMapOrNetworkWithState,
  useTokensFromMapOrNetwork,
} from 'lib/hooks/useCurrency'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { getTokenFilter } from 'lib/hooks/useTokenList/filtering'
import { filterLowValueTokens, tokenComparator, useSortTokensByQuery } from 'lib/hooks/useTokenList/sorting'
import { useMemo } from 'react'
import { WrappedTokenInfo } from 'state/lists/wrappedTokenInfo'
import { filterNonZeroTokensBalances } from 'utils/filterNonZeroTokensBalances'
import { isLPToken } from 'utils/isLPToken'
import { filters } from 'utils/tokenFilters'

import { TokenAddressMap, useCombinedTokenList } from '../state/lists/hooks'
import { useUserAddedTokens } from '../state/user/hooks'

// reduce token map into standard address <-> Token mapping, optionally include user added tokens
function useTokensFromMap(
  tokenMap: TokenAddressMap,
  includeUserAdded: boolean
): { [address: string]: WrappedTokenInfo } {
  const { chainId } = useConnection()
  const userAddedTokens = useUserAddedTokens()

  return useMemo(() => {
    if (!chainId) return {}

    // reduce to just tokens
    const mapWithoutUrls = Object.keys(tokenMap[chainId] ?? {}).reduce<{ [address: string]: WrappedTokenInfo }>(
      (newMap, address) => {
        newMap[address] = tokenMap[chainId][address].token
        return newMap
      },
      {}
    )

    if (includeUserAdded) {
      return (
        (userAddedTokens as WrappedTokenInfo[])
          // reduce into all ALL_TOKENS filtered by the current chain
          .reduce<{ [address: string]: WrappedTokenInfo }>(
            (tokenMap, token) => {
              if (tokenMap[token.address]) return tokenMap
              tokenMap[token.address] = token
              return tokenMap
            },
            // must make a copy because reduce modifies the map, and we do not
            // want to make a copy in every iteration
            { ...mapWithoutUrls }
          )
      )
    }

    return mapWithoutUrls
  }, [chainId, userAddedTokens, tokenMap, includeUserAdded])
}

export function useAllTokens(exclude?: string[]): { [address: string]: WrappedTokenInfo } {
  const allTokens = useCombinedTokenList()
  const list = useTokensFromMap(allTokens, true)
  return useMemo(() => {
    if (exclude) {
      const excluded = new Set(exclude.filter(Boolean).map((address) => address.toLowerCase()))
      const filteredList = Object.keys(list).reduce<{ [address: string]: WrappedTokenInfo }>((acc, address) => {
        if (!excluded.has(address.toLowerCase())) {
          acc[address] = list[address]
        }
        return acc
      }, {})
      return filteredList
    }
    return list
  }, [list, exclude])
}

export const useTokensByCategories = (
  tokens: WrappedTokenInfo[],
  categoriesKeys: CategoryKey[] | null,
  balances: {
    [tokenAddress: string]: CurrencyAmount<Token> | undefined
  }
): WrappedTokenInfo[] => {
  return useMemo(() => {
    if (!categoriesKeys) return tokens

    const matchesMainCategory = (token: WrappedTokenInfo): boolean => {
      if (isLPToken(token) || !filters.main(token)) return false

      const balance = balances[token.address]
      const hasNonZeroBalance = balance && !balance.equalTo(0)

      const mainCategory = CATEGORIES_MAP[CATEGORY_KEYS.MAIN]
      const matchesMainFilter = mainCategory?.filter ? mainCategory.filter(token) : false

      return hasNonZeroBalance || matchesMainFilter
    }

    const matchesCategory = (token: WrappedTokenInfo, categoryKey: CategoryKey): boolean => {
      if (categoryKey === CATEGORY_KEYS.MAIN) {
        return matchesMainCategory(token)
      }

      const category = CATEGORIES_MAP[categoryKey]
      return category?.filter ? category.filter(token) : false
    }

    if (Array.isArray(categoriesKeys)) {
      if (categoriesKeys.length === 0) return tokens

      return tokens.filter((token) => categoriesKeys.some((categoryKey) => matchesCategory(token, categoryKey)))
    }

    return tokens
  }, [tokens, categoriesKeys, balances])
}

export function useIsTokenActive(token: Token | undefined | null): boolean {
  const activeTokens = useAllTokens()

  if (!activeTokens || !token) {
    return false
  }

  return !!activeTokens[token.address]
}

// Check if currency is included in custom list from user storage
export function useIsUserAddedToken(currency: Currency | undefined | null): boolean {
  const userAddedTokens = useUserAddedTokens()

  if (!currency) {
    return false
  }

  return !!userAddedTokens.find((token) => currency.equals(token))
}

// undefined if invalid or does not exist
// null if loading or null was passed
// otherwise returns the token
export function useToken(tokenAddress?: string | null): Token | null | undefined {
  const tokens = useAllTokens()
  return useTokenFromMapOrNetwork(tokens, tokenAddress)
}

export function useSearchToken(tokenAddress?: string | null): {
  token: Token | null | undefined
  isLoading: boolean
  error: string | undefined
} {
  const tokens = useAllTokens()
  const {
    token,
    isLoading: isTokenLoading,
    error: errorFromNetwork,
  } = useTokenFromMapOrNetworkWithState(tokens, tokenAddress)

  return {
    token,
    isLoading: isTokenLoading,
    error: errorFromNetwork,
  }
}

export function useTokens(tokenAddresses?: (string | null | undefined)[]): Token[] {
  const tokens = useAllTokens()
  const result = useTokensFromMapOrNetwork(tokens, tokenAddresses) as Token[]
  if (!tokenAddresses || tokenAddresses.length === 0) return []
  return result
}

export function useCurrency(currencyId?: string | null): Currency | null | undefined {
  const tokens = useAllTokens()
  return useCurrencyFromMap(tokens, currencyId)
}

export function useFilteredSortedTokens(params: {
  tokens: WrappedTokenInfo[]
  selectedCategories: CategoryKey[]
  balances: {
    data: {
      [tokenAddress: string]: CurrencyAmount<Token> | undefined
    }
    isLoading: boolean
  }
  tokenPrices: {
    data: (number | null)[] | null
    isLoading: boolean
  }
  allowNativeToken?: boolean
  debouncedQuery?: string
  /** Hold the list back until balances load so it doesn't reorder; defaults to waiting while they load. */
  waitForBalances?: boolean
  allowedCurrencies?: string[]
  preferredCurrencies?: Currency[]
  showCommonBases?: boolean
  nonZeroBalances?: boolean
}): {
  filteredSortedTokens: Currency[]
  filteredSortedTokensWithNative: Currency[]
  useSmartFilter: boolean
  hasMultipleCategoryResults: boolean
} {
  const {
    tokens,
    selectedCategories,
    balances,
    tokenPrices,
    allowNativeToken,
    debouncedQuery,
    waitForBalances,
    allowedCurrencies,
    preferredCurrencies,
    showCommonBases,
    nonZeroBalances,
  } = params
  const tokenAddresses = useMemo(() => tokens.map((token) => token.wrapped.address), [tokens])

  const tokensByCategory = useTokensByCategories(tokens, selectedCategories, balances.data)

  const tokenPriceMap = useMemo(() => {
    if (!tokenPrices.data || !Array.isArray(tokenPrices.data)) return {}

    const priceMap: { [address: string]: number | null } = {}
    tokenAddresses.forEach((address, index) => {
      priceMap[address] = tokenPrices.data?.[index] !== undefined ? tokenPrices.data[index] : null
    })
    return priceMap
  }, [tokenAddresses, tokenPrices])

  const basicFilteredFromCategory: Token[] = useMemo(() => {
    return Object.values(tokensByCategory).filter(getTokenFilter(debouncedQuery ?? ''))
  }, [debouncedQuery, tokensByCategory])

  const basicFilteredFromAll: Token[] = useMemo(() => {
    return Object.values(tokens).filter(getTokenFilter(debouncedQuery ?? ''))
  }, [debouncedQuery, tokens])

  const useSmartFilter = useMemo(() => {
    if (!debouncedQuery?.trim() || selectedCategories.length === 0) {
      return false
    }
    return basicFilteredFromCategory.length < basicFilteredFromAll.length || basicFilteredFromCategory.length === 0
  }, [debouncedQuery, selectedCategories.length, basicFilteredFromCategory.length, basicFilteredFromAll.length])

  const usingTokens = useMemo(() => {
    return useSmartFilter ? tokens : tokensByCategory
  }, [useSmartFilter, tokens, tokensByCategory])

  const filteredTokens: Token[] = useMemo(() => {
    let basicFiltered = Object.values(usingTokens).filter(getTokenFilter(debouncedQuery ?? ''))

    if (nonZeroBalances && balances.data) {
      basicFiltered = filterNonZeroTokensBalances(basicFiltered, Object.values(balances.data))
    }

    if (!debouncedQuery && !tokenPrices.isLoading && Object.keys(tokenPriceMap).length > 0) {
      return filterLowValueTokens(basicFiltered, balances.data, tokenPriceMap, 0.01, false)
    }

    return basicFiltered
  }, [debouncedQuery, tokenPrices.isLoading, usingTokens, balances, tokenPriceMap, nonZeroBalances])

  const sortedTokens: Token[] = useMemo(() => {
    if (waitForBalances ?? balances.isLoading) return []

    return [...filteredTokens].sort((a, b) => tokenComparator(balances.data, tokenPriceMap, a, b))
  }, [balances, filteredTokens, tokenPriceMap, waitForBalances])

  const filteredSortedTokens = useSortTokensByQuery(debouncedQuery ?? '', sortedTokens)

  const native = useNativeCurrency()

  const filteredSortedTokensWithNative: Currency[] = useMemo(() => {
    let filtered = filteredSortedTokens

    if (allowedCurrencies?.length) {
      filtered = filtered.filter((token) => token.isToken && allowedCurrencies.includes(token.address))
    }

    if (!native || (!allowNativeToken && allowedCurrencies?.length)) {
      return filtered
    }

    const s = debouncedQuery?.toLowerCase().trim()
    if (
      (!s || native.symbol?.toLowerCase()?.indexOf(s) !== -1) &&
      (!selectedCategories.length || selectedCategories.includes(nativeCategoryForChain(native.chainId)))
    ) {
      // Check if native is in preferred currencies
      const nativeInPreferred = preferredCurrencies?.some((currency) => currency.isNative)
      if (!nativeInPreferred || !showCommonBases) {
        return [native, ...filtered]
      }
    }
    return filtered
  }, [
    debouncedQuery,
    native,
    filteredSortedTokens,
    allowedCurrencies,
    allowNativeToken,
    preferredCurrencies,
    showCommonBases,
    selectedCategories,
  ])

  const hasMultipleCategoryResults = useMemo(
    () => basicFilteredFromCategory.length > 0 && basicFilteredFromCategory.length < basicFilteredFromAll.length,
    [basicFilteredFromCategory.length, basicFilteredFromAll.length]
  )

  return {
    filteredSortedTokens,
    filteredSortedTokensWithNative,
    useSmartFilter,
    hasMultipleCategoryResults,
  }
}
